﻿import { executeLegacyMethod, isLegacyMethod } from './compatCuiMethods';
import cuiCommands from './commands/index';
import getMethod from '../metadata/method';
import itemTypeMetadata from '../metadata/itemType';
import getResource from '../core/resources';
import alert from '../components/alert';
import CUIError from './CUIError';
import AsyncFunction from '../core/AsyncFunction';
import { SOAPError } from '../core/Soap';

// Get the error names. These need to be compared to the errors that appear from CUI methods, since instanceof doesn't work correctly among iframes.
const cuiErrorName = new CUIError().name;
const soapErrorName = new SOAPError().name;

const getErrorMessage = (type, handlerData) => {
	if (type === 'command') {
		const { commandAlias } = handlerData;
		return getResource('cui.failed_to_execute_command', commandAlias);
	}

	const { methodId } = handlerData;
	const { name } = getMethod(methodId);
	return getResource('aras_object.method_failed', name);
};

const alertError = (error, handler, handlerData) => {
	if (error.name === cuiErrorName) {
		const { message } = error;
		return alert(message);
	}

	if (error.name === soapErrorName) {
		const xml = error.xml;
		const data = new SOAPResults(aras, xml);
		return alert('', {
			type: 'soap',
			data
		});
	}

	const { type } = handler;
	const message = getErrorMessage(type, handlerData);
	const { message: technicalMessage } = error;
	const stackTrace = getResource('common.client_side_err');
	return alert(message, {
		type: 'stack',
		technicalMessage,
		stackTrace
	});
};

const notifyError = (message) => {
	const notify = aras.getNotifyByContext(window);
	notify(message, { type: 'error' });
};

const createHandler = (handlerData, context, options) => {
	const { methodId, commandAlias, type } = handlerData;
	if (methodId) {
		const method = getMethod(methodId);
		const code = method?.method_code;
		if (code !== '') {
			return cuiMethods.createClientMethod(handlerData, context, options);
		}
	}

	if (!commandAlias) {
		return null;
	}

	const { commandFactory } = cuiCommands;
	const command = commandFactory(commandAlias, options);
	if (!command) {
		const message = getResource('cui.failed_to_find_command', commandAlias);
		notifyError(message);
		return null;
	}

	const commandMethod = type === 'init' ? 'canExecute' : 'execute';
	const commandExecutor = command[commandMethod];
	return {
		method: commandExecutor,
		type: 'command'
	};
};

const cuiMethods = {
	collectCuiData: function (items, initialData) {
		const data = Object.assign({}, initialData, {
			dataMap: new Map(),
			events: new Set(['click']),
			images: [],
			roots: []
		});

		return items.reduce(function (data, item) {
			item = cuiMethods.initializeItem(item, data);
			if (item.data) {
				const itemsMap = new Map();
				item.data.forEach(function (childItem) {
					childItem = cuiMethods.initializeItem(childItem, data, item);
					cuiMethods.addItemPropertiesToCuiData(data, childItem);
					itemsMap.set(childItem.id, childItem);
				});
				item.data = itemsMap;
			}
			cuiMethods.addItemPropertiesToCuiData(data, item);
			data.dataMap.set(item.id, item);
			data.roots.push(item.name);
			return data;
		}, data);
	},
	addItemPropertiesToCuiData: function (cuiData, item) {
		if (item.clickEventType) {
			if (item.clickEventType === 'mousedown') {
				cuiData.events.add('keydown');
			}
			cuiData.events.add(item.clickEventType);
		}
		if (item.image) {
			cuiData.images.push(item.image);
		}
		if (item.image_additional) {
			cuiData.images.push(item.image_additional);
		}
	},
	destroyMethodsCollection: new WeakMap(),
	destroyMethodFactory: function (control, detachEventListenersArray = []) {
		const destroy = () => {
			cuiMethods.destroyMethodsCollection.delete(control);
			detachEventListenersArray.forEach((fn) => fn());
		};
		return destroy;
	},
	defaultCuiItemHandler: function (control, options, itemId, event) {
		let parentItem;
		let targetItem;
		const menuItemNode = event.target.closest('li[data-index]');
		if (menuItemNode) {
			parentItem = control.data.get(itemId);
			const targetId = menuItemNode.dataset.index;
			targetItem = parentItem.data.get(targetId);
		} else {
			targetItem = control.data.get(itemId);
		}

		if (!targetItem) {
			return;
		}

		const clickEventType = targetItem.clickEventType;
		const eventType = event.type;
		const context = {
			currentTarget: parentItem || targetItem,
			target: targetItem,
			control: control
		};

		if (
			targetItem['@type'] === 'CommandBarButton' &&
			clickEventType === 'mousedown' &&
			['keydown'].includes(eventType) &&
			['Enter', 'NumpadEnter', 'Space', 13, 32].includes(
				event.code || event.keyCode
			)
		) {
			cuiMethods.executeClickMethod(targetItem, context, options);
			return;
		}

		if (
			clickEventType === eventType ||
			(!clickEventType && eventType === 'click')
		) {
			cuiMethods.executeClickMethod(targetItem, context, options);
		}
	},
	createClientMethod: function (handlerData, context, options = {}) {
		const { methodId, type } = handlerData;
		const method = getMethod(methodId);
		if (!method) {
			const message = getResource('cui.failed_to_execute_method', methodId);
			notifyError(message);
			return null;
		}

		if (!options.commandFactory) {
			const commandFactory = cuiMethods.getLayoutCommandFactory(options);
			context = {
				...context,
				commandFactory
			};
		}

		const isInit = type === 'init';
		const FunctionConstructor = isInit ? Function : AsyncFunction;
		Object.assign(context, options);
		const methodCode = method.method_code;
		const methodExecutor = () => {
			const isLegacy = isLegacyMethod(methodCode, context);
			if (isLegacy) {
				return executeLegacyMethod(methodId, methodCode, context);
			}

			const method = new FunctionConstructor(
				'control',
				'target',
				'options',
				methodCode
			); // jshint ignore:line
			return method(context.control, context.target, context);
		};
		return {
			method: methodExecutor,
			type: 'method'
		};
	},
	executeClickMethod: async (item = {}, ...args) => {
		const {
			'command_alias@keyed_name': commandAlias,
			on_click_handler: clickHandlerId,
			on_keydown_handler: keydownHandlerId
		} = item;
		const methodId = clickHandlerId || keydownHandlerId;
		const handlerData = {
			methodId,
			commandAlias,
			type: 'click'
		};
		const handler = createHandler(handlerData, ...args);
		if (!handler) {
			return;
		}

		const { method } = handler;
		try {
			const result = await method();
			if (Array.isArray(result)) {
				const rejected = result.filter((promise) => {
					return promise?.status === 'rejected' && promise?.reason;
				});
				for (const promise of rejected) {
					const { reason } = promise;
					await alertError(reason, handler, handlerData);
				}
			}

			return result;
		} catch (error) {
			alertError(error, handler, handlerData);
		}
	},
	executeInitMethod: (item = {}, ...args) => {
		const {
			'command_alias@keyed_name': commandAlias,
			on_init_handler: methodId
		} = item;
		const handlerData = {
			methodId,
			commandAlias,
			type: 'init'
		};

		const handler = createHandler(handlerData, ...args);
		if (!handler) {
			return;
		}

		const { method } = handler;
		try {
			return method();
		} catch (error) {
			alertError(error, handler, handlerData);
		}
	},
	getConfigurableUi: async function (location, options) {
		const requestParams = await cuiMethods.getRequestParams(location, options);
		return aras.MetadataCacheJson.GetConfigurableUiAsync(requestParams);
	},
	getRequestParams: async (location, options) => {
		const {
			itemId = '',
			itemTypeName = '',
			item_classification: itemClassification = ''
		} = options;

		const itemTypeId = itemTypeMetadata.getItemTypeId(itemTypeName);
		const presentableItem = await isPresentableItem(itemTypeId);

		return {
			item_id: presentableItem ? itemId : '',
			item_type_id: itemTypeId,
			location_name: location,
			item_classification: itemClassification
		};
	},
	getLayoutCommandFactory: ({ itemTypeName, location, itemType }) => {
		const { commandFactory } = cuiCommands;
		const predefinedOptions = {
			itemType,
			itemTypeName,
			location
		};
		return (type, options) => {
			const commandOptions = { ...predefinedOptions, ...options };
			const { itemType, itemTypeName } = commandOptions;
			if (itemType && itemType.name !== itemTypeName) {
				throw new Error(
					`The name of the itemType object doesn't match the name passed to the command`
				);
			}

			return commandFactory(type, commandOptions);
		};
	},
	handleControlEvents: function (control, events, options, handler) {
		if (!handler) {
			return;
		}
		if (cuiMethods.destroyMethodsCollection.has(control)) {
			throw new Error('The event handler for this control is already defined.');
		}

		const controlEventHandler = (...args) => {
			const controlData = cuiMethods.destroyMethodsCollection.get(control);
			const handlerOptions = controlData.options;

			return handler(control, handlerOptions, ...args);
		};
		const detachEventListenersArray = [...events].map((eventName) => {
			if (control.on) {
				return control.on(eventName, controlEventHandler);
			}

			control.addEventListener(eventName, controlEventHandler);
			return () => control.removeEventListener(eventName, controlEventHandler);
		});

		const destroy = cuiMethods.destroyMethodFactory(
			control,
			detachEventListenersArray
		);

		cuiMethods.destroyMethodsCollection.set(control, {
			destroy,
			options
		});

		return {
			destroy
		};
	},
	initializeCuiControl: function (control, location, cuiItemTypes, options) {
		const destroyMethodsCollection = cuiMethods.destroyMethodsCollection;
		if (destroyMethodsCollection.has(control)) {
			destroyMethodsCollection.get(control).destroy();
			destroyMethodsCollection.delete(control);
		}

		return cuiMethods
			.getConfigurableUi(location, options)
			.then(function (items) {
				const cuiData = cuiMethods.collectCuiData(items, {
					control,
					cuiItemTypes,
					options
				});
				ArasModules.SvgManager.load(cuiData.images);
				return cuiData;
			});
	},
	initializeItem: function (item, cuiData, rootItem) {
		const context = {
			control: cuiData.control,
			currentTarget: rootItem || item,
			target: item
		};
		const type = item['@type'];
		const itemTypeMetadata = cuiData.cuiItemTypes[type];
		const initHandlerResult = cuiMethods.executeInitMethod(
			item,
			context,
			cuiData.options
		);
		Object.assign(
			item,
			itemTypeMetadata,
			item.additional_data,
			initHandlerResult
		);

		item.id = item.name;
		return item;
	},
	reinitControlItems: function (control, eventType, options) {
		const context = {
			control: control
		};
		const reinitAllItems = function (data, rootItem) {
			data.forEach(function (item) {
				const events = item.include_events;
				if (events && events.indexOf(eventType) !== -1) {
					initItem(data, item, rootItem);
				}
			});
		};
		const initItem = function (data, item, rootItem) {
			Object.assign(context, {
				currentTarget: rootItem || item,
				target: item
			});
			const optionsForInit =
				options || cuiMethods.destroyMethodsCollection.get(control).options;
			const initHandlerResult = cuiMethods.executeInitMethod(
				item,
				context,
				optionsForInit
			);
			const newItem = Object.assign(
				{},
				item,
				item.additional_data,
				initHandlerResult
			);
			data.set(item.id, newItem);

			if (item.data) {
				reinitAllItems(item.data, item);
			}
		};

		reinitAllItems(control.data);
		control.render();
	},
	adaptDataForControl: function (cuiData) {
		const data = cuiData.dataMap;
		data.forEach(function (item) {
			if (item.image) {
				item.icon = item.image;
				delete item.image;
			}
			if (!item.data) {
				return;
			}
			item.data.forEach(function (child) {
				data.set(child.id, child);
			});
			item.children = item.roots;
			delete item.data;
			delete item.roots;
		});
		return cuiData;
	}
};

const isPresentableItem = async (itemTypeId) => {
	if (!itemTypeId) {
		return false;
	}

	const itemType = await itemTypeMetadata.getItemType('PresentableItems');
	const morphaeList = itemTypeMetadata.getMorphaeList(itemType);
	return morphaeList.some((item) => item.id === itemTypeId);
};

export default cuiMethods;
